import React from 'react';
import { useForm } from 'react-hook-form';
import { PagSeguro as Icon, SimpleCheck } from '@profitfy/pakkun-icons';
import { yupResolver } from '@hookform/resolvers/yup';
import { useHistory, useParams } from 'react-router-dom';

import { EHeadingWeight } from '@domain/enums/components/EHeading';
import { ETextSize } from '@domain/enums/components/EText';
import { IParams } from '@domain/interfaces/IParams';

import { useEcommerce } from '@helpers/hooks/pages/onboarding/useEcommerce';
import { useToast } from '@helpers/hooks/useToast';

import { getTranslatedIntegrationsError } from '@helpers/utils/errors/integrations/integrations';

import { pagseguroSchema } from '@helpers/validators/dashboard/integrationCenter/pagseguro';

import pagseguroCredentialService from '@services/pages/dashboard/integrationCenter/gateway/pagseguro';

import NextStep from '@components/Onboarding/NextStep';
import Text from '@components/common/core/DataDisplay/Text';

import * as S from './styles';

const PagSeguro: React.FC = () => {
  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm({ mode: 'onBlur', resolver: yupResolver(pagseguroSchema) });
  const { handleNextGatewayToIntegrate, selectedMarketings } = useEcommerce();
  const { storeAliasId } = useParams<IParams>();
  const history = useHistory();
  const { toast } = useToast();

  const [isLoadingSubmittedForm, setIsLoadingSubmittedForm] = React.useState<boolean>(false);
  const [emailValue, setEmailValue] = React.useState<string>('');
  const [accessTokenValue, setAccessTokenValue] = React.useState<string>('');

  const emailRegister = register('email');
  const accessTokenRegister = register('access_token');

  const onEmailValueChange = React.useCallback(
    event => {
      emailRegister.onChange(event);

      setEmailValue(event.target.value);
    },
    [emailRegister],
  );

  const onAccessTokenValueChange = React.useCallback(
    event => {
      accessTokenRegister.onChange(event);

      setAccessTokenValue(event.target.value);
    },
    [accessTokenRegister],
  );

  const onSubmit = React.useCallback(
    async data => {
      setIsLoadingSubmittedForm(true);

      try {
        await pagseguroCredentialService.createCredential({
          storeAliasId,
          data,
        });

        toast.success('Credencial salva com sucesso!');

        setIsLoadingSubmittedForm(false);

        const hasNextGateway = handleNextGatewayToIntegrate();

        if (!hasNextGateway && selectedMarketings.length) {
          history.push(`/onboarding/${storeAliasId}/ecommerce/integrations/marketing`);
        }

        if (!hasNextGateway && !selectedMarketings.length) {
          history.push(`/onboarding/${storeAliasId}/ecommerce/integrations/integration-finished`);
        }
      } catch (error: any) {
        setIsLoadingSubmittedForm(false);
        toast.error(getTranslatedIntegrationsError(error));
      }
    },
    [history, handleNextGatewayToIntegrate, storeAliasId, selectedMarketings, toast],
  );

  const hasAllInputsValue = emailValue && accessTokenValue;

  return (
    <S.Wrapper>
      <S.FormWrapper>
        <S.Form onSubmit={handleSubmit(onSubmit)}>
          <S.Header>
            <S.Title>Integre com Gateway de pagamento</S.Title>
          </S.Header>

          <S.IntegrationTitle>
            <Icon size={36} />

            <S.IntegrationTitleText fontWeight={EHeadingWeight.REGULAR}>
              Integração com PagSeguro
            </S.IntegrationTitleText>
          </S.IntegrationTitle>

          <S.Body>
            <S.BenefitsWrapper>
              <S.Benefit>
                <SimpleCheck size={12} />
                <S.DescriptionText size={ETextSize.B5}>Exatidão dos dados</S.DescriptionText>
              </S.Benefit>
              <S.Benefit>
                <SimpleCheck size={12} />
                <S.DescriptionText size={ETextSize.B5}>Confiabilidade dos dados</S.DescriptionText>
              </S.Benefit>
            </S.BenefitsWrapper>

            <S.InputGroup>
              <S.Label>Email</S.Label>
              <S.Input
                {...emailRegister}
                placeholder="E-mail PagSeguro"
                type="text"
                id="pagseguro-email"
                onChange={onEmailValueChange}
                value={emailValue}
                isError={errors.email}
              />
              {errors.email && <Text isErrorFeedback>{errors.email.message}</Text>}
            </S.InputGroup>

            <S.InputGroup>
              <S.Label>Access Token</S.Label>
              <S.Input
                {...accessTokenRegister}
                placeholder="Token de Acesso PagSeguro"
                type="text"
                id="pagseguro-access-token"
                onChange={onAccessTokenValueChange}
                value={accessTokenValue}
                isError={errors.access_token}
              />
              {errors.access_token && <Text isErrorFeedback>{errors.access_token.message}</Text>}
            </S.InputGroup>
          </S.Body>

          <NextStep
            type="submit"
            isLoading={isLoadingSubmittedForm}
            disabled={isLoadingSubmittedForm || !hasAllInputsValue}
          >
            Próximo passo
          </NextStep>
        </S.Form>
      </S.FormWrapper>

      <S.TutorialWrapper>
        <S.TutorialImage
          src="https://s3.us-west-2.amazonaws.com/app.profitfy.me-media/onboarding/images/content/onboarding/onboarding-integration-tutorial.svg"
          alt="Profitfy.me Integration Tutorial"
        />
        <S.TutorialTitle>Tutorial de integração</S.TutorialTitle>
        <S.TutorialContent>
          Para realizar a integração você precisará do <strong>Email</strong> e do{' '}
          <strong>Access Token</strong> que são encontrados neste link:
        </S.TutorialContent>
        <S.TutorialLink
          href="https://ajuda.profitfy.me/pt-br/articles/6009972-como-integrar-com-o-pagseguro"
          target="_blank"
        >
          Encontre suas credenciais clicando aqui!
        </S.TutorialLink>
      </S.TutorialWrapper>
    </S.Wrapper>
  );
};

export default PagSeguro;
